import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import logger from '%app/js/common/services/Logger';
import parseJsonAttribute from '%app/js/common/services/parseJsonAttribute';
import readLocalization from '../services/readLocalization';

const DEFAULT_SERVER_DATA = Object.freeze({
    environment: '',
    country: '',
    locale: '',
    userHash: '',
    appSettings: Object.freeze({}),
});

const ServerDataContext = createContext(DEFAULT_SERVER_DATA);

const APP_SETTINGS_ATTRIBUTES = ['data-redesign-app-settings', 'data-app-settings'];

function readAppSettings(html) {
    const attribute = APP_SETTINGS_ATTRIBUTES.find(name => html.getAttribute(name) !== null);
    if (attribute === undefined) {
        return {};
    }

    return parseJsonAttribute(
        html,
        attribute,
        {},
        error => logger.error(`Failed to parse ${attribute} attribute`, { error }),
    );
}

function readServerData() {
    const html = document.documentElement;
    const { locale, country } = readLocalization();

    return {
        ...DEFAULT_SERVER_DATA,
        environment: html.getAttribute('data-environment') || DEFAULT_SERVER_DATA.environment,
        country,
        locale,
        userHash: html.getAttribute('data-user-hash') || DEFAULT_SERVER_DATA.userHash,
        appSettings: readAppSettings(html),
    };
}

function ServerDataProvider({ children, value = null }) {
    const serverData = useMemo(() => value || readServerData(), [value]);

    return (
        <ServerDataContext.Provider value={serverData}>
            {children}
        </ServerDataContext.Provider>
    );
}

ServerDataProvider.propTypes = {
    children: PropTypes.node.isRequired,
    value: PropTypes.shape({
        environment: PropTypes.string,
        country: PropTypes.string,
        locale: PropTypes.string,
        userHash: PropTypes.string,
        appSettings: PropTypes.objectOf(PropTypes.any),
    }),
};

function useServerData() {
    return useContext(ServerDataContext);
}

export default ServerDataContext;
export {
    ServerDataProvider,
    useServerData,
    readServerData,
    DEFAULT_SERVER_DATA,
};
